import { acceptHMRUpdate, defineStore } from 'pinia'
import { Screen } from 'quasar'
import { computed, ref } from 'vue'
import { shallowRef } from 'vue'

import { defaultSortingOptionSlug } from '@/composables/filter/sortingOptions'
import type { Breakpoint, PublicBannerFragment } from '@/gql/graphql'
import type { ConfigFeature, IndexConfigResponse } from '@/types/api/config'
import type { Filter } from '@/types/api/filter'
import type { ResolvePageRouteResponse } from '@/types/api/pageRoute'
import type { IndexSearchResponse, ShowMapResponse } from '@/types/api/search'
import type { ComposableSPListItem } from '@/types/async'
import type { SearchResultsMeta } from '@/types/search'

export const useAppStore = defineStore(
  'app',
  () => {
    const APP_BASE_URL = process.env.APP_BASE_URL

    const appBaseUrl = computed(() =>
      process.env.NODE_ENV === 'development'
        ? `${APP_BASE_URL}:${process.env.APP_PORT}`
        : APP_BASE_URL,
    )

    const activeLocale = ref<string | null>(null)

    const apolloState = ref<string>('')

    const pageNotFound = ref<boolean>(false)

    const config = shallowRef<IndexConfigResponse['data']>()
    const composableSPListItem = shallowRef<ComposableSPListItem>()

    const configCountry = computed(() => config.value?.country)
    const configLocale = computed(() => config.value?.locale)
    const configTheme = computed(() => config.value?.theme)

    const defaultCategory = computed(
      () => config.value?.categories.find((v) => v.default)?.slug,
    )
    const defaultCommercialization = computed(
      () => config.value?.commercializations.find((v) => v.default)?.slug,
    )

    /** Configured map zoom default (from `configTheme.page.search.map.options.zoom`). */
    const defaultMapZoom = computed(
      () => configTheme.value?.page.search.map.options.zoom,
    )

    /**
     * Strip-when-default helper for URL query writes.
     *
     * Returns `undefined` when `value` matches the store default — Vue Router
     * omits `undefined` query values, keeping the URL clean. Use whenever you
     * are about to push a value into `route.query` that mirrors a configured
     * default (e.g. `sort_by=weight:desc`).
     */
    const sortByForUrl = (value: string | null | undefined) => {
      if (value === undefined || value === null) return undefined
      return value === defaultSortingOption.value ? undefined : value
    }

    /**
     * Strip-when-default helper for URL query writes.
     *
     * Returns `undefined` when `value` matches the configured map zoom.
     * Vue Router omits `undefined` values so the URL stays clean.
     */
    const mapZoomForUrl = (value: number | null | undefined) => {
      if (value === undefined || value === null) return undefined
      return value === defaultMapZoom.value ? undefined : value
    }

    const optionsCategories = computed<string[]>(
      () => config.value?.categories.map((c) => c.slug) || [],
    )

    const optionsCommercializations = computed<string[]>(
      () => config.value?.commercializations.map((c) => c.slug) || [],
    )

    const showOnlyMap = ref<boolean>(false)

    /**
     * Banner breakpoint chosen during SSR. Replayed on the client through
     * hydration so we don't mismatch Quasar's server `Screen.name` (`xs`)
     * against the real viewport after `onSSRHydrated`.
     */
    const bannerBreakpoint = ref<Breakpoint | null>(null)

    const loadingListingsMap = ref<boolean>(false)
    const loadingListingsMeta = ref<boolean>(false)
    const loadingListingsSearch = ref<boolean>(false)

    const resolvedPageRoute = ref<ResolvePageRouteResponse['data']>()

    /**
     * Default `sort_by` slug for the active commercialization. Falls back to
     * the config default commercialization when no page route is resolved
     * (favorites, CP, 404).
     */
    const defaultSortingOption = computed(() =>
      defaultSortingOptionSlug(
        config.value?.sorting_options,
        resolvedPageRoute.value?.route.commercialization
          ?? defaultCommercialization.value,
      ),
    )

    const responseListingsMap = ref<ShowMapResponse>()
    const responseListingsMeta = ref<SearchResultsMeta>()
    const responseListingsSearch = ref<IndexSearchResponse>()

    /**
     * Filter schema for the active category × commercialization.
     * Lives in Pinia so SSR-prefetched filters survive hydration —
     * a module-level ref would reset to `[]` on the client.
     */
    const filters = shallowRef<Filter[]>([])
    const filtersLoading = ref<boolean>(false)
    /** `${category}:${commercialization}` of the most recent successful bootFilters. */
    const filtersBootKey = ref<string | null>(null)

    /**
     * SPList banners, prefetched in `bootSearch` so they survive hydration.
     * Apollo cache is not serialized (`extractApolloState`).
     */
    const searchListBanners = shallowRef<PublicBannerFragment[]>([])
    /** `${category}:${commercialization}:${sortedLocationSlugs}` */
    const searchListBannersBootKey = ref<string | null>(null)

    const showOnlyMapKey = computed<'true' | 'false'>(
      () => String(showOnlyMap.value) as 'true' | 'false',
    )

    const configPropsShowOnlyMap = computed(
      () => configTheme.value?.page.search.component_props.show_only_map,
    )

    const shouldFetchClusters = computed<boolean>(
      () =>
        !!(
          process.env.CLIENT
          && configPropsShowOnlyMap.value
          && configPropsShowOnlyMap.value[showOnlyMapKey.value][Screen.name].map
        ),
    )

    const shouldFetchSearch = computed<boolean>(
      () =>
        !!(
          configPropsShowOnlyMap.value
          && configPropsShowOnlyMap.value[showOnlyMapKey.value][Screen.name]
            .list
        ),
    )

    // Prefer search over clusters: on desktop both are true, but list totals
    // are SSR-hydrated while map clusters are client-only.
    const searchResultsMetaTotal = computed<number>(() => {
      if (shouldFetchSearch.value) {
        return responseListingsSearch.value?.meta.total ?? 0
      }

      if (shouldFetchClusters.value) {
        return responseListingsMap.value?.meta.total ?? 0
      }

      return 0
    })

    const getFeature = (featureName: ConfigFeature['name']) =>
      config.value?.features.find((el) => el.name === featureName)

    return {
      activeLocale,
      apolloState,
      appBaseUrl,
      bannerBreakpoint,
      composableSPListItem,
      config,
      configCountry,
      configLocale,
      configTheme,
      defaultCategory,
      defaultCommercialization,
      defaultMapZoom,
      defaultSortingOption,
      filters,
      filtersBootKey,
      filtersLoading,
      getFeature,

      loadingListingsMap,
      loadingListingsMeta,
      loadingListingsSearch,
      mapZoomForUrl,
      optionsCategories,
      optionsCommercializations,
      pageNotFound,
      resolvedPageRoute,
      responseListingsMap,
      responseListingsMeta,
      responseListingsSearch,
      searchListBanners,
      searchListBannersBootKey,
      searchResultsMetaTotal,
      shouldFetchClusters,
      shouldFetchSearch,
      showOnlyMap,
      sortByForUrl,
    }
  },
  {
    share: {
      enable: !!process.env.CLIENT,
      initialize: !!process.env.CLIENT,
      omit: [
        'activeLocale',
        'apolloState',
        'bannerBreakpoint',
        'config',
        'composableSPListItem',
        'filters',
        'filtersBootKey',
        'filtersLoading',
        'loadingListingsMeta',
        'loadingListingsMap',
        'loadingListingsSearch',
        'pageNotFound',
        'resolvedPageRoute',
        'responseListingsMap',
        'responseListingsMeta',
        'responseListingsSearch',
        'searchListBanners',
        'searchListBannersBootKey',
        'showOnlyMap',
      ],
    },
  },
)

if (import.meta.hot && process.env.NODE_ENV === 'development') {
  import.meta.hot.accept(acceptHMRUpdate(useAppStore, import.meta.hot))
}
